import {produce} from 'immer';
import queryClient from '@/utils/reactQueryClient';
import type {
  CreatorBrandSafetyVetting,
  CreatorBrandSafetyVettingStatus,
} from '@/types/creatorBrandSafetyVetting';
import {CreatorSafetyReport} from '@/types/creatorSafetyReport';
import {queryKey as creatorBrandSafetyVettingsQueryKey} from '@/hooks/queries/use-creator-brand-safety-vettings-query';
import {queryKey as vettingReportQueryKey} from '@/hooks/queries/use-vetting-report-query/useVettingReportQuery';

interface UpdateVettingReportStatusParams {
  creatorId: string;
  reportId: string;
  status: CreatorBrandSafetyVettingStatus;
}

export const updateVettingReportStatus = ({
  creatorId,
  reportId,
  status,
}: UpdateVettingReportStatusParams) => {
  const queryKey = [vettingReportQueryKey, creatorId, reportId];
  const previousData = queryClient.getQueryData(queryKey);

  queryClient.setQueryData(
    queryKey,
    (old: {data: CreatorSafetyReport} | undefined) => {
      if (!old) {
        return old;
      }
      return produce(old, (draft) => {
        if (draft) {
          draft.data.vettingStatus = status;
        }
      });
    }
  );
  return previousData;
};

interface UpdateVettingReportsStatusParams {
  brandId: string;
  vettingId: string;
  status: CreatorBrandSafetyVettingStatus;
}

type InfiniteQueryData = {
  pages: {data: CreatorBrandSafetyVetting[]}[];
  pageParams: unknown[];
};

export const updateCreatorBrandSafetyVettingsStatus = ({
  brandId,
  vettingId,
  status,
}: UpdateVettingReportsStatusParams) => {
  const queryKeyPrefix = [creatorBrandSafetyVettingsQueryKey, brandId];
  const matchingQueries = queryClient.getQueriesData<InfiniteQueryData>({
    queryKey: queryKeyPrefix,
  });

  const previousDataMap = new Map(matchingQueries);

  matchingQueries.forEach(([key, data]) => {
    if (!data?.pages) return;

    queryClient.setQueryData(
      key,
      produce(data, (draft) => {
        draft.pages.some((page) => {
          const vettingRow = page.data?.find(
            (vetting) => vetting.id === vettingId
          );
          if (vettingRow) {
            vettingRow.status = status;
            return true;
          }
          return false;
        });
      })
    );
  });

  return previousDataMap;
};
